// Staff membership is no longer in the ServicePro document. The session still
// resolves scope from GET /auth/staff/me, so this is the local shape of that read.
export interface Membership {
  businessId: string;
  businessName: string;
  role: string;
  siteId: string | null;
  activated: boolean;
}
export type SiteOption = { id: string; name: string };

// Which business (and then which site) this session is scoped to, derived from
// GET /auth/staff/me instead of the build-time VITE_BUSINESS_ID/VITE_SITE_ID this app used
// to require. Both decisions are pure functions so every branch — including the ones a UI
// test would struggle to stage — is unit-testable, the same way StaffAccessRules is on the
// backend. The React component around them (ScopeGate) stays a thin adapter.

export type BusinessResolution =
  | { kind: "none" }
  | { kind: "one"; membership: Membership }
  | { kind: "pick"; options: Membership[] };

export function resolveBusiness(memberships: Membership[], selected: string | null): BusinessResolution {
  if (memberships.length === 0) return { kind: "none" };
  if (memberships.length === 1) return { kind: "one", membership: memberships[0] };

  // A selection is only honoured if it is still a real membership: access can be revoked
  // between sessions, and a stale (or hand-edited) value must fall back to the picker
  // rather than scope the app into a business the caller no longer belongs to.
  const chosen = memberships.find((m) => m.businessId === selected);
  return chosen ? { kind: "one", membership: chosen } : { kind: "pick", options: memberships };
}

export type SiteResolution =
  | { kind: "none" }
  | { kind: "fixed"; siteId: string }
  | { kind: "pick"; options: SiteOption[] };

export function resolveSite(
  membership: Membership, sites: SiteOption[], selected: string | null,
): SiteResolution {
  // A site manager's scope is not theirs to choose — their RoleAssignment names the site,
  // and BusinessAccessMiddleware would 403 any other one. No picker, no selection.
  if (membership.role === "SiteManager") {
    return membership.siteId ? { kind: "fixed", siteId: membership.siteId } : { kind: "none" };
  }

  if (sites.length === 0) return { kind: "none" };
  if (sites.length === 1) return { kind: "fixed", siteId: sites[0].id };

  const chosen = sites.find((s) => s.id === selected);
  return chosen ? { kind: "fixed", siteId: chosen.id } : { kind: "pick", options: sites };
}
